import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";

import {
  useNumberSequences,
  useResetNumberSequence,
  useUpdateNumberSequence,
} from "@/hooks/use-number-sequences";

import {
  numberSequenceSchema,
  type NumberSequenceFormValues,
} from "./number-sequence-schema";
import {
  generateSequencePreview,
  getDefaultConfigForModule,
  REGISTERED_MODULES,
  SEQUENCE_FORMAT_PRESETS,
  SEQUENCE_PLACEHOLDERS,
} from "./number-sequence-utils";

export type ModuleSummary = NumberSequenceFormValues & { nextPreview: string };

interface UseNumberSequenceOptions {
  defaultModule?: string;
  onSaved?: () => void;
}

export function useNumberSequence(
  workspaceId: string,
  { defaultModule = "sale", onSaved }: UseNumberSequenceOptions = {},
) {
  const [selectedModuleCode, setSelectedModuleCode] = useState(defaultModule);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);

  const { data: apiSummaries, isLoading } = useNumberSequences(workspaceId);
  const updateMutation = useUpdateNumberSequence();
  const resetMutation = useResetNumberSequence();

  const configFor = useCallback(
    (moduleCode: string): NumberSequenceFormValues => {
      const fromApi = apiSummaries?.find((s) => s.moduleCode === moduleCode);
      if (!fromApi) return getDefaultConfigForModule(moduleCode);
      return {
        moduleCode: fromApi.moduleCode,
        name: fromApi.name,
        prefix: fromApi.prefix,
        suffix: fromApi.suffix,
        size: fromApi.size,
        step: fromApi.step,
        next: fromApi.next,
        resetPeriod: fromApi.resetPeriod,
        autoGenerateOnCreate: fromApi.autoGenerateOnCreate,
      };
    },
    [apiSummaries],
  );

  const moduleSummaries = useMemo<ModuleSummary[]>(
    () =>
      REGISTERED_MODULES.map((mod) => {
        const cfg = configFor(mod.moduleCode);
        const fromApi = apiSummaries?.find(
          (s) => s.moduleCode === mod.moduleCode,
        );
        return {
          ...cfg,
          nextPreview: fromApi?.nextPreview ?? generateSequencePreview(cfg),
        };
      }),
    [apiSummaries, configFor],
  );

  const activeModuleInfo = useMemo(
    () =>
      REGISTERED_MODULES.find((m) => m.moduleCode === selectedModuleCode) ??
      null,
    [selectedModuleCode],
  );

  const form = useForm<NumberSequenceFormValues>({
    resolver: zodResolver(numberSequenceSchema),
    defaultValues: getDefaultConfigForModule(defaultModule),
  });

  // Re-sync the form whenever the selected module or server data changes
  useEffect(() => {
    form.reset(configFor(selectedModuleCode));
  }, [selectedModuleCode, configFor, form]);

  const watched = useWatch({ control: form.control });
  const previewCode = useMemo(() => {
    const base = configFor(selectedModuleCode);
    return generateSequencePreview({
      prefix: watched.prefix ?? base.prefix,
      suffix: watched.suffix ?? base.suffix,
      size: Number(watched.size ?? base.size),
      next: Number(watched.next ?? base.next),
    });
  }, [watched, configFor, selectedModuleCode]);

  const handleResetCounter = useCallback(async () => {
    try {
      await resetMutation.mutateAsync({
        moduleCode: selectedModuleCode,
        body: { nextNumber: 1 },
        workspaceId,
      });
      toast.success(
        `Counter ${activeModuleInfo?.name ?? selectedModuleCode} di-reset ke 1.`,
      );
    } catch {
      toast.error("Gagal me-reset counter penomoran.");
    } finally {
      setIsResetDialogOpen(false);
    }
  }, [resetMutation, selectedModuleCode, activeModuleInfo, workspaceId]);

  const handleResetToDefault = useCallback(() => {
    form.reset({
      ...getDefaultConfigForModule(selectedModuleCode),
      next: form.getValues("next"),
    });
    form.trigger();
    toast.info("Format dikembalikan ke default. Klik Simpan untuk menerapkan.");
  }, [form, selectedModuleCode]);

  const insertPlaceholder = useCallback(
    (field: "prefix" | "suffix", tag: string) => {
      const current = form.getValues(field) || "";
      form.setValue(field, `${current}${tag}`, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form],
  );

  const applyPreset = useCallback(
    (prefix: string, suffix: string) => {
      form.setValue("prefix", prefix, {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue("suffix", suffix, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form],
  );

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await updateMutation.mutateAsync({
        moduleCode: values.moduleCode,
        body: {
          name: values.name,
          prefix: values.prefix,
          suffix: values.suffix,
          size: values.size,
          step: values.step,
          next: values.next,
          resetPeriod: values.resetPeriod,
          autoGenerateOnCreate: values.autoGenerateOnCreate,
        },
        workspaceId,
      });
      toast.success(
        `Penomoran ${activeModuleInfo?.name ?? values.moduleCode} berhasil disimpan.`,
      );
      onSaved?.();
    } catch {
      toast.error("Gagal menyimpan pengaturan penomoran.");
    }
  });

  return {
    form,
    isLoading,
    selectedModuleCode,
    setSelectedModuleCode,
    activeModuleInfo,
    moduleSummaries,
    registeredModules: REGISTERED_MODULES,
    placeholders: SEQUENCE_PLACEHOLDERS,
    presets: SEQUENCE_FORMAT_PRESETS,
    previewCode,
    isSaving: updateMutation.isPending,
    isResetDialogOpen,
    openResetDialog: () => setIsResetDialogOpen(true),
    closeResetDialog: () => setIsResetDialogOpen(false),
    handleResetCounter,
    handleResetToDefault,
    insertPlaceholder,
    applyPreset,
    onSubmit,
  };
}
